import type { ReactNode } from "react";

function H2({ children }: { children: ReactNode }) {
	return (
		<h2
			className="mt-10 font-mono text-[clamp(1.15rem,2.2vw,1.4rem)] leading-[1.2] tracking-[-0.01em] text-foreground"
			style={{ fontWeight: 400 }}
		>
			{children}
		</h2>
	);
}

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-[1.65] text-muted-foreground">
			{children}
		</p>
	);
}

function A({ href, children }: { href: string; children: ReactNode }) {
	return (
		<a
			href={href}
			className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
		>
			{children}
		</a>
	);
}

function Note({ children }: { children: ReactNode }) {
	return (
		<div className="mt-6 border border-border bg-card p-4">
			<p className="text-base leading-[1.6] text-muted-foreground">
				{children}
			</p>
		</div>
	);
}

function Table({ head, rows }: { head: string[]; rows: string[][] }) {
	return (
		<div className="mt-6 overflow-x-auto">
			<table className="w-full min-w-[30rem] border-collapse font-mono text-sm">
				<thead>
					<tr>
						{head.map((cell) => (
							<th
								key={cell}
								className="border-b border-border px-3 py-2 text-left text-xs uppercase tracking-[0.1em] text-foreground"
							>
								{cell}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row[0]}>
							{row.map((cell, i) => (
								<td
									key={cell}
									className={
										i === 0
											? "border-b border-border px-3 py-2 text-foreground"
											: "border-b border-border px-3 py-2 text-muted-foreground"
									}
								>
									{cell}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function WhatAiAnswersCitePost() {
	return (
		<>
			<P>
				Every guide on getting recommended by AI tells you the same thing: get
				into the roundups. Earn placements in other people's "best tools for X"
				posts, because that is what the models read.
			</P>
			<P>
				That advice is half right, and which half depends entirely on which
				engine your buyers use. Here is the data.
			</P>

			<H2>The corpus</H2>
			<P>
				<A href="https://verifieddr.com">VerifiedDR</A> runs tracked buyer
				questions against ChatGPT, Perplexity and Google AI Mode on a schedule
				and keeps every source each answer cites. In August 2026 it classified
				the whole archive by page type: 16,322 distinct pages across 21,508
				citation events.
			</P>
			<P>
				The first finding is a counting trap worth understanding before you read
				any other number. Measured per distinct URL, the mix reads 42% homepage,
				because 1,884 of those citations point at a bare hostname and each one
				is a single URL. Weighted by citation events, which is what a reader
				actually encounters, it looks different:
			</P>
			<Table
				head={["Page type", "Share of citation events"]}
				rows={[
					["Listicle / roundup", "17.1%"],
					["Homepage", "14.6%"],
					["Product page", "12.0%"],
				]}
			/>
			<P>
				So the roundup advice is not wrong. Listicles are the single most cited
				page type across the archive. But they are 17.1%, not a majority, and
				the average hides the thing that matters.
			</P>

			<H2>ChatGPT and Perplexity are mirror images</H2>
			<P>
				Split the same corpus by engine and the two biggest slices invert
				completely.
			</P>
			<Table
				head={["Page type", "ChatGPT", "Perplexity"]}
				rows={[
					["Homepage", "33.1%", "12.1%"],
					["Listicle / roundup", "6.8%", "20.0%"],
				]}
			/>
			<P>
				ChatGPT reaches for the brand's own homepage a third of the time and
				almost never cites a roundup. Perplexity does the opposite. These are
				not variations on a strategy. They are two different distribution
				channels that happen to share a product category.
			</P>
			<Note>
				One caveat, stated plainly: ChatGPT is 3,294 of those 21,508 events,
				because it only records sources on full paid runs. Perplexity carries
				most of the corpus. Treat the ChatGPT column as directional rather than
				precise.
			</Note>

			<H2>What this changes for an agent product</H2>
			<P>
				If you sell an AI agent, you are in a category where the buyer's first
				move is increasingly to ask another AI which one to use. That makes the
				split above a budget decision, not a curiosity.
			</P>
			<P>
				<strong className="text-foreground">
					If your buyers live in ChatGPT
				</strong>
				, your own pages do the work. A third of citations go to a homepage, and
				another 12% to product pages. The money belongs in making those pages
				answer the question directly: what the agent does, what it costs, what
				it connects to, who it is for. Not in outreach.
			</P>
			<P>
				<strong className="text-foreground">
					If your buyers live in Perplexity
				</strong>
				, one citation in five is somebody else's roundup and your own homepage
				barely registers. Getting named in the directories and comparison posts
				of your category is the actual work, and it looks more like digital PR
				than like content marketing.
			</P>
			<P>
				<strong className="text-foreground">
					If you do not know which one your buyers use
				</strong>
				, find out before you spend anything. Ten questions asked manually in
				both engines, logged out, with the date recorded, will tell you more
				than a quarter of guessing.
			</P>

			<H2>The measurement problem underneath</H2>
			<P>
				Agent products have a specific version of this problem. Revenue claims
				are easy to make and hard to check, which is why AgentMRR exists. Being
				recommended by an AI answer has the same shape: it is easy to assert
				that a model likes your product, and hard to show it.
			</P>
			<P>
				Both are fixed the same way, by keeping a fixed question set and a dated
				record rather than a single score. Report the share of unbranded buyer
				questions where you appear, the cited sources you do not own, and any
				factual error the model repeats about you. Resist building one blended
				visibility number: generation is sampled and engines personalize, so the
				number moves for reasons you cannot explain and you spend the next
				quarter defending noise.
			</P>
			<P>
				If you want the recurring version of this rather than a manual check,{" "}
				<A href="https://verifieddr.com/ai-visibility">VerifiedDR tracks it</A>{" "}
				across those three engines with every answer shown verbatim and the
				cited pages listed, on the same profile as the backlink and trust data
				that decides whether you are citable at all.
			</P>
		</>
	);
}
